import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * VideoCard with TikTok-style interactions:
 * - Double-tap to like with animated heart
 * - Long-press for action menu
 * - Smooth engagement animations
 */

const VIEW_SECONDS_THRESHOLD = 3;
const VIEW_COMPLETION_THRESHOLD = 0.35;

const normalizeMediaUrl = (url, secureContext) => {
  if (!url) return '';
  if (!secureContext) return url;
  if (url.startsWith('https://') || url.startsWith('//')) return url;
  if (/^http:\/\//i.test(url)) {
    return url.replace('http://', 'https://');
  }
  return url;
};

const getCreatorId = (post = {}) =>
  post.user_id ?? post.userId ?? post.creator_id ?? post.creatorId;

export default function VideoCard({
  post,
  index,
  isActive,
  soundOn,
  secureContext,
  user,
  isAuthenticated,
  onToggleSound,
  onLike,
  onFollow,
  onComment,
  onShare,
  onViewProgress,
  onVideoRef,
}) {
  const videoRef = useRef(null);
  const [showHeart, setShowHeart] = useState(false);
  const [heartPosition, setHeartPosition] = useState({ x: 0, y: 0 });
  const [lastTap, setLastTap] = useState(0);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const viewTrackedRef = useRef(false);
  const watchProgressRef = useRef(0);

  const mediaSrc = normalizeMediaUrl(post.content_url, secureContext);
  const creatorId = getCreatorId(post);
  const canFollow = isAuthenticated && creatorId && creatorId !== user?.id;

  // Register video ref with parent
  useEffect(() => {
    if (videoRef.current && onVideoRef) {
      onVideoRef(post.id, videoRef.current);
    }
    return () => {
      if (onVideoRef) onVideoRef(post.id, null);
    };
  }, [post.id, onVideoRef]);

  // Handle active state - play/pause
  useEffect(() => {
    if (!videoRef.current) return;

    if (isActive) {
      videoRef.current.play().catch((err) => console.log('Auto-play failed:', err));
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isActive]);

  // Handle mute state
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = !soundOn || !isActive;
    }
  }, [soundOn, isActive]);

  // Double-tap to like detection
  const handleTap = useCallback((e) => {
    const now = Date.now();
    if (now - lastTap < 300) {
      // Double tap detected!
      const rect = e.currentTarget.getBoundingClientRect();
      setHeartPosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setShowHeart(true);
      
      if (!post.is_liked && onLike) {
        onLike(post, 'like');
      }
      
      // Haptic feedback
      navigator.vibrate?.(50);
      
      setTimeout(() => setShowHeart(false), 1000);
    }
    setLastTap(now);
  }, [lastTap, post, onLike]);

  // Long press for action menu using useGesture
  const longPressTimer = useRef(null);
  
  const handlePointerDown = useCallback(() => {
    longPressTimer.current = setTimeout(() => {
      setShowActionMenu(true);
      navigator.vibrate?.(100);
    }, 500);
  }, []);

  const handlePointerUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handlePointerLeave = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // Track watch progress
  const handleTimeUpdate = useCallback((e) => {
    if (viewTrackedRef.current) return;

    const video = e.target;
    const duration = video.duration || Number(post.duration_seconds) || 0;
    const currentTime = video.currentTime;
    watchProgressRef.current = currentTime;

    const meetsDuration = currentTime >= VIEW_SECONDS_THRESHOLD;
    const meetsCompletion = duration > 0 && currentTime / duration >= VIEW_COMPLETION_THRESHOLD;

    if (meetsDuration || meetsCompletion) {
      viewTrackedRef.current = true;
      onViewProgress?.(post.id, {
        watched_seconds: Math.round(currentTime),
        duration_seconds: duration ? Math.round(duration) : null,
        percent_viewed: duration ? Number((currentTime / duration).toFixed(2)) : null,
      });
    }
  }, [post, onViewProgress]);

  // Toggle play/pause on video click
  const handleVideoClick = useCallback((e) => {
    // Don't toggle if this was a double tap
    if (Date.now() - lastTap < 350) return;
    
    if (videoRef.current.paused) {
      videoRef.current.play().catch((err) => console.log('Play failed:', err));
    } else {
      videoRef.current.pause();
    }
  }, [lastTap]);

  const actionStackStyle = {
    bottom: `calc(20px + var(--safe-area-bottom, 0px))`,
  };

  return (
    <div
      className="video-item relative w-full bg-black overflow-hidden"
      style={{ height: '100vh', minHeight: '100vh' }}
      onClick={handleTap}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
    >
      {mediaSrc && (
        <video
          ref={videoRef}
          data-index={index}
          src={mediaSrc}
          className="absolute inset-0 w-full h-full object-contain md:object-contain"
          style={{
            backgroundColor: '#000',
            maxHeight: '100%',
            maxWidth: '100vw'
          }}
          muted={!soundOn || !isActive}
          loop
          playsInline
          preload={index === 0 ? 'auto' : 'metadata'}
          onClick={handleVideoClick}
          onTimeUpdate={handleTimeUpdate}
          onError={(e) => {
            console.error('❌ Video failed to load:', mediaSrc, e.target.error?.message);
          }}
        />
      )}

      {/* Animated Heart on Double-Tap */}
      <AnimatePresence>
        {showHeart && (
          <motion.div
            initial={{ scale: 0, opacity: 1 }}
            animate={{ scale: 1.5, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="absolute pointer-events-none z-30"
            style={{
              left: heartPosition.x - 50,
              top: heartPosition.y - 50,
            }}
          >
            <span className="text-8xl drop-shadow-2xl">❤️</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Right side action buttons */}
      <div className="absolute right-4 flex flex-col gap-6 z-20" style={actionStackStyle}>
        <motion.button
          whileTap={{ scale: 0.9 }}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSound?.();
          }}
          className="flex flex-col items-center gap-1 text-white"
        >
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-xl">
            {soundOn ? '🔊' : '🔈'}
          </div>
          <span className="text-sm font-semibold">{soundOn ? 'Sound on' : 'Tap for sound'}</span>
        </motion.button>

        <motion.button
          whileTap={{ scale: 0.9 }}
          animate={post.is_liked ? { scale: [1, 1.2, 1] } : {}}
          type="button"
          aria-pressed={post.is_liked}
          onClick={(e) => {
            e.stopPropagation();
            if (isAuthenticated) {
              onLike?.(post, post.is_liked ? 'unlike' : 'like');
            }
          }}
          className={`flex flex-col items-center gap-1 text-white ${post.is_liked ? 'text-red-400' : ''}`}
        >
          <div className={`w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-2xl ${post.is_liked ? 'bg-red-500/30' : ''}`}>
            {post.is_liked ? '❤️' : '🤍'}
          </div>
          <span className="text-sm font-semibold">{post.like_count || 0}</span>
        </motion.button>

        <motion.button
          whileTap={{ scale: 0.9 }}
          className="flex flex-col items-center gap-1 text-white"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onComment?.(post);
          }}
        >
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-2xl">
            💬
          </div>
          <span className="text-sm font-semibold">{post.comment_count || 0}</span>
        </motion.button>

        <motion.button
          whileTap={{ scale: 0.9 }}
          className="flex flex-col items-center gap-1 text-white"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onShare?.(post);
          }}
        >
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-2xl">
            📤
          </div>
          <span className="text-sm font-semibold">{post.share_count || 0}</span>
        </motion.button>
      </div>

      {/* Video Info Overlay */}
      <div className="absolute left-0 right-16 bottom-0 p-6 pb-32 bg-gradient-to-t from-black/60 via-black/20 to-transparent z-10 pointer-events-none">
        <div className="text-white">
          <div className="flex items-center gap-3 mb-3 pointer-events-auto">
            <Link to={`/profile/${post.username}`} className="flex items-center gap-2 hover:opacity-80">
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-sm font-bold overflow-hidden"
              >
                {post.avatar_url ? (
                  <img src={post.avatar_url} className="w-full h-full object-cover" alt="" />
                ) : (
                  post.username?.[0]?.toUpperCase() || 'U'
                )}
              </motion.div>
              <span className="font-semibold">@{post.username}</span>
              {post.is_verified && <span className="text-blue-400">✓</span>}
            </Link>
            {canFollow && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onFollow?.(post);
                }}
                className={`px-4 py-1 rounded-full text-sm font-semibold border transition ${
                  post.is_following
                    ? 'border-white/40 text-white/80 bg-white/10'
                    : 'border-blue-400 text-white bg-blue-500/30 hover:bg-blue-500/50'
                }`}
              >
                {post.is_following ? 'Following' : 'Follow'}
              </motion.button>
            )}
          </div>
          <p className="text-base mb-2 line-clamp-3">{post.caption}</p>
          <div className="flex gap-4 text-xs text-gray-300">
            <span>👁️ {post.view_count || 0} views</span>
          </div>
        </div>
      </div>

      {/* Long-Press Action Menu */}
      <AnimatePresence>
        {showActionMenu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              setShowActionMenu(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-slate-900 rounded-3xl p-6 w-72 space-y-2"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-white font-bold text-lg mb-4 text-center">Actions</h3>
              
              <button className="w-full py-3 px-4 rounded-xl bg-white/10 text-white hover:bg-white/20 transition flex items-center gap-3">
                <span className="text-xl">🔖</span>
                <span>Save Video</span>
              </button>
              
              <button className="w-full py-3 px-4 rounded-xl bg-white/10 text-white hover:bg-white/20 transition flex items-center gap-3">
                <span className="text-xl">🚫</span>
                <span>Not Interested</span>
              </button>
              
              <button className="w-full py-3 px-4 rounded-xl bg-white/10 text-white hover:bg-white/20 transition flex items-center gap-3">
                <span className="text-xl">⬇️</span>
                <span>Download</span>
              </button>
              
              <button className="w-full py-3 px-4 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30 transition flex items-center gap-3">
                <span className="text-xl">🚨</span>
                <span>Report</span>
              </button>

              <button
                onClick={() => setShowActionMenu(false)}
                className="w-full py-3 px-4 rounded-xl bg-white/5 text-white/60 hover:bg-white/10 transition mt-4"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
